import { motion, AnimatePresence } from "framer-motion";
import PaperImage from "../assets/images/paper.png";
import BackgroundImage from "../../../components/ui/BackgroundImage";
import CopyToClipboard from "../../../components/CopyToClipboard";
import { useEffect, useRef, useState } from "react";
import ArrestOptions from "../components/ArrestOptions";
import ArrestSignature from "./Signature";
import offenseCategories from "../data/offenseCategories.json";

interface ArrestDocumentProps {
  className?: string;
  logo?: string;
  title: string;
  // description: string;
  fineAmount: number;
  prisonDuration: string;
  boldWords?: string[];
  officerData: {
    name: string;
    surname: string;
    rank: string;
  };
  detaineeData: {
    name: string;
    surname: string;
    cnp: number | string;
  };
  badgeImage: string;
  initialSignature?: string | null;
  onSave?: (payload: any) => void;
}

const ArrestDocument = ({
  className = "",
  title,
  // description,
  fineAmount,
  prisonDuration,
  officerData,
  detaineeData,
  badgeImage,
  initialSignature = null,
  logo,
  boldWords,
  onSave,
}: ArrestDocumentProps) => {
  const [canvasWidth, setCanvasWidth] = useState(0);
  const [closing, setClosing] = useState(false);

  const signatureParentRef = useRef<HTMLDivElement>(null);
  const signatureCanvas = useRef<any>(null);

  const [inSignatureBox, setInSignatureBox] = useState<boolean>(false);
  const [hasSignature, setHasSignature] = useState<boolean>(false);

  const [formState, setFormState] = useState<
    Record<string, string | string[] | null>
  >(() => {
    const init: Record<string, string | string[] | null> = {};
    offenseCategories.forEach((c) => (init[c.key] = null));
    return init;
  });

  // used to retrigger shake animations
  const [shakeKey, setShakeKey] = useState(0);

  useEffect(() => {
    const resize = () => {
      if (signatureParentRef.current) {
        setCanvasWidth(signatureParentRef.current.offsetWidth);
      }
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  useEffect(() => {
    if (!initialSignature) return;
    const t = setTimeout(() => {
      try {
        if (signatureCanvas.current?.fromDataURL) {
          signatureCanvas.current.fromDataURL(initialSignature);
          setHasSignature(true);
        }
      } catch {}
    }, 150);
    return () => clearTimeout(t);
  }, [initialSignature]);

  const handleClearSignature = () => {
    signatureCanvas.current?.clear?.();
    setHasSignature(false);
  };

  const handleSave = () => {
    // ▶ VALIDATION: at least one offense must be selected
    const hasAtLeastOneOffense = Object.values(formState).some(
      (v) => v !== null && v !== "" && !(Array.isArray(v) && v.length === 0)
    );

    if (!hasAtLeastOneOffense) {
      // clear signature so user will re-sign after selecting reasons
      try {
        signatureCanvas.current?.clear?.();
      } catch {}
      setHasSignature(false);

      // trigger shake animation
      setShakeKey((s) => s + 1);
      return; // stop saving
    }

    // ▶ proceed with animation and saving
    setClosing(true);

    setTimeout(() => {
      const offenses: Record<string, string | string[] | null> = {};
      offenseCategories.forEach((c) => {
        offenses[c.key] = formState[c.key] ?? null;
      });

      let signatureBase64: string | null = null;
      try {
        const data = signatureCanvas.current?.toDataURL?.();
        if (data && data.length > 1000) signatureBase64 = data;
        else if (initialSignature) signatureBase64 = initialSignature;
      } catch {}

      const payload = {
        detaineeData,
        officerData,
        offenses,
        fineAmount,
        prisonDuration,
        signatureBase64,
        generatedAt: new Date().toISOString(),
      };

      onSave?.(payload);
    }, 400);
  };

  const renderDescription = () => {
    const date = new Date().toLocaleDateString();
    const officerRankAndName = `${officerData.rank} - ${officerData.name}`;
    const arrestedPersonName = `${detaineeData.name}`;
    const arrestedPersonCNP = `${detaineeData.cnp}`;

    return (
      <>
        {" "}
        Întocmit astăzi, <span className="font-semibold">{date}</span>, de
        agentul constatator{" "}
        <span className="font-semibold">{officerRankAndName}</span> Persoana
        identificată ca{" "}
        <span className="font-semibold">{arrestedPersonName}</span>,{" "}
        <span className="font-semibold">{arrestedPersonCNP}</span>, este
        arestată ca urmare a constatării următoarelor fapte care încalcă
        legislația în vigoare. Având în vedere gravitatea faptelor și
        necesitatea împiedicării sustragerii de la răspundere penală, se dispune
        privarea de libertate a persoanei menționate
      </>
    );
  };

  const renderDescriptionText = () => {
    const date = new Date().toLocaleDateString();
    const officerRankAndName = `${officerData.rank} - ${officerData.name}`;
    const arrestedPersonName = `${detaineeData.name}`;
    const arrestedPersonCNP = `${detaineeData.cnp}`;

    return `Întocmit astăzi, ${date}, de agentul constatator ${officerRankAndName}. Persoana identificată ca ${arrestedPersonName}, ${arrestedPersonCNP}, este arestată ca urmare a constatării următoarelor fapte care încalcă legislația în vigoare. Având în vedere gravitatea faptelor și necesitatea împiedicării sustragerii de la răspundere penală, se dispune privarea de libertate a persoanei menționate.`;
  };

  return (
    <AnimatePresence>
      {!closing && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.85 }}
          transition={{ duration: 0.4 }}
        >
          <BackgroundImage
            imageSrc={PaperImage}
            imageClassName="!select-none drop-shadow-xl"
            className={`tms-font relative ${className}`}
          >
            <div className="w-[600px] min-h-[720px] z-[1] p-4 px-7 flex flex-col justify-between">
              {logo && (
                <img
                  className="mx-auto object-contain mb-2 mt-1"
                  src={logo}
                  alt="logo"
                />
              )}

              <h1 className="text-[25px] tracking-wider text-[#535353] text-center mb-5">
                {title}
              </h1>

              <div className="w-full h-[1.7px] bg-[#AEAEAE]/80" />

              <p className="text-left text-[17px] text-[#535353] my-5 mb-3 leading-[1.3]">
                {renderDescription()}
              </p>

              <div className="flex items-center space-x-3">
                <div className="w-full h-[1.8px] bg-[#AEAEAE]/80" />
                <CopyToClipboard text={String(renderDescriptionText())} />
              </div>

              {/* Offenses area: shake when validation fails */}
              <motion.div
                key={`offenses-area-${shakeKey}`}
                initial={false}
                animate={{ x: [0, -7, 7, -5, 5, 0] }}
                transition={{ duration: 0.45 }}
                className="min-h-[140px] flex flex-col gap-1 mt-3"
              >
                {offenseCategories.map((cat) => (
                  <ArrestOptions
                    key={cat.key}
                    label={cat.label}
                    options={cat.options}
                    multiple={cat.multiple}
                    value={formState[cat.key] ?? null}
                    onChange={(val) =>
                      setFormState((prev) => ({ ...prev, [cat.key]: val }))
                    }
                  />
                ))}

                <div className="flex items-center justify-between my-3 mb-4">
                  <p className="text-[15px] text-[#535353]">
                    Fine:{" "}
                    <span className="font-semibold">
                      ${fineAmount.toLocaleString()}
                    </span>
                  </p>
                  <p className="text-[15px] text-[#535353]">
                    Prison:{" "}
                    <span className="font-semibold">{prisonDuration}</span>
                  </p>
                </div>
              </motion.div>

              <div className="flex items-start justify-between relative gap-8">
                <div className="flex-1">
                  <h2 className="text-[13px] text-[#535353] uppercase">
                    DETAINEE DATA:
                  </h2>
                  <section className="space-y-2 mt-1">
                    <div className="flex items-center space-x-2">
                      <span className="text-black text-[15px]">Name:</span>
                      <p className="w-full border-b border-[#AEAEAE] text-[#535353]">
                        {detaineeData.name}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-black text-[15px]">Surname:</span>
                      <p className="w-full border-b border-[#AEAEAE] text-[#535353]">
                        {detaineeData.surname}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-black text-[15px]">CNP:</span>
                      <p className="w-full border-b border-[#AEAEAE] text-[#535353]">
                        {detaineeData.cnp}
                      </p>
                    </div>
                  </section>
                </div>

                <div className="w-[1px] bg-[#AEAEAE]/25 h-full absolute left-[50%] translate-x-[-50%]" />

                <div className="flex-1">
                  <h2 className="text-[13px] text-[#535353] uppercase">
                    OFFICER DATA:
                  </h2>

                  <section className="space-y-2 mt-1">
                    <div className="flex items-center space-x-2">
                      <span className="text-black text-[15px]">Name:</span>
                      <p className="w-full border-b border-[#AEAEAE] text-[#535353]">
                        {officerData.name}
                      </p>
                    </div>

                    <div className="flex items-center space-x-2">
                      <span className="text-black text-[15px]">Surname:</span>
                      <p className="w-full border-b border-[#AEAEAE] text-[#535353]">
                        {officerData.surname}
                      </p>
                    </div>

                    <div className="flex items-center space-x-2">
                      <span className="text-black text-[15px]">Rank:</span>
                      <p className="w-full bg-[#DEDEDE] rounded-lg py-2 px-3 text-[#464646] max-w-fit text-[14px]">
                        {officerData.rank}
                      </p>
                    </div>
                  </section>

                  {/* Signature + badge area */}
                  <div className="w-full flex items-center space-x-3 mt-3 relative">
                    <img
                      src={badgeImage}
                      className="w-[87px] h-[85px] object-contain"
                      alt="badge"
                    />

                    <div className="flex-1">
                      <ArrestSignature
                        signatureCanvas={signatureCanvas}
                        signatureParentRef={signatureParentRef}
                        canvasWidth={canvasWidth}
                        inSignatureBox={inSignatureBox}
                        setInSignatureBox={setInSignatureBox}
                        hasSignature={hasSignature}
                        setHasSignature={setHasSignature}
                        onClear={handleClearSignature}
                        onDone={handleSave}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </BackgroundImage>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ArrestDocument;
